import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../Modal';
import { format } from "date-fns";
import { EVENT_TYPES, EVENT_STATUS } from "../../../../contexts/EventContext";

const EventModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  mode = "create",
}) => {
  // Memoize initial event data
  const getInitialEventData = useCallback(() => ({
    id: "",
    title: "",
    start: new Date(),
    end: new Date(),
    allDay: false,
    type: "CLIENT",
    extendedProps: {
      attendees: [],
      virtualMeetingLink: "",
      status: EVENT_STATUS.SCHEDULED,
      location: "",
      description: "",
    },
  }), []);

  const [eventData, setEventData] = useState(getInitialEventData);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEventData(getInitialEventData());
    }
  }, [isOpen, getInitialEventData]);

  // Update form when initialData changes
  useEffect(() => {
    if (initialData && isOpen) {
      setEventData(initialData);
    }
  }, [initialData, isOpen]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    onSubmit(eventData);
  }, [eventData, onSubmit]);

  if (!isOpen) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      width="large"
    >
      <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-neutral-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {mode === "create" ? "Create Event" : "Edit Event"}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          ✕
        </button>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <div>
          <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-1">
            Title
          </label>
          <input 
            type="text" 
            value={eventData.title}
            onChange={(e) =>
              setEventData({ ...eventData, title: e.target.value })
            }
            className="w-full p-2 border rounded-lg text-[14px]"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Date & Time
            </label>
            <input
              type="datetime-local"
              value={format(new Date(eventData.start), "yyyy-MM-dd'T'HH:mm")}
              onChange={(e) =>
                setEventData({
                  ...eventData,
                  start: new Date(e.target.value),
                })
              }
              className="w-full p-2 border rounded-lg text-[14px]"
              required
            />
          </div>
          <div>
            <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Date & Time
            </label>
            <input
              type="datetime-local"
              value={format(new Date(eventData.end), "yyyy-MM-dd'T'HH:mm")}
              onChange={(e) =>
                setEventData({ ...eventData, end: new Date(e.target.value) })
              }
              className="w-full p-2 border rounded-lg text-[14px]"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-1">
            Event Type
          </label>
          <select
            value={eventData.type}
            onChange={(e) =>
              setEventData({
                ...eventData,
                type: e.target.value,
                backgroundColor: EVENT_TYPES[e.target.value].color,
                borderColor: EVENT_TYPES[e.target.value].borderColor,
              })
            }
            className="w-full p-2 border rounded-lg text-[14px]"
          >
            {Object.entries(EVENT_TYPES).map(([key, value]) => (
              <option key={key} value={key}>
                {value.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-1">
            Location
          </label>
          <input
            type="text"
            value={eventData.extendedProps.location}
            onChange={(e) =>
              setEventData({
                ...eventData,
                extendedProps: {
                  ...eventData.extendedProps,
                  location: e.target.value,
                },
              })
            }
            className="w-full p-2 border rounded-lg text-[14px]"
          />
        </div>
        <div>
          <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea 
            value={eventData.extendedProps.description}
            onChange={(e) =>
              setEventData({
                ...eventData,
                extendedProps: {
                  ...eventData.extendedProps,
                  description: e.target.value,
                },
              })
            }
            rows={4}
            className="w-full p-2 border rounded-lg resize-none text-[14px]"
          />
        </div>
        <div className="flex justify-end space-x-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[14px] text-gray-700 dark:text-gray-300 bg-gray-200 hover:bg-gray-300 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-3 py-1.5 text-[14px] bg-[#029831] text-white rounded-lg hover:bg-[#018529]"
          >
            {mode === "create" ? "Create Event" : "Update Event"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EventModal;
